import { useState } from 'react';
import { Card, CardContent } from '../ui/Card';
import { RichTextEditor, RichTextDisplay } from '../ui/RichTextEditor';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  TrashIcon,
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  BuildingOfficeIcon,
  BriefcaseIcon,
  StarIcon,
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import { cn } from '../../utils/cn';
import type { InvestigationSubject } from '../../services/investigations.service';

interface SubjectCardProps {
  subject: InvestigationSubject;
  index: number;
  isEditing: boolean;
  canDelete: boolean;
  onChange: (index: number, field: keyof InvestigationSubject, value: string | boolean) => void;
  onDelete: (index: number) => void;
  onSetPrimary: (index: number) => void;
}

export function SubjectCard({
  subject,
  index,
  isEditing,
  canDelete,
  onChange,
  onDelete,
  onSetPrimary,
}: SubjectCardProps) {
  const [isExpanded, setIsExpanded] = useState(true);

  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  const hasContent = subject.name || subject.email || subject.phone || subject.role || subject.department || subject.notes;

  return (
    <Card className="mb-3 border border-secondary-200 dark:border-secondary-700">
      <div
        className={cn(
          'flex items-center justify-between p-3 cursor-pointer',
          'hover:bg-secondary-50 dark:hover:bg-secondary-800/50',
          'rounded-t-lg transition-colors'
        )}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center space-x-3">
          <UserIcon className="w-5 h-5 text-secondary-500 dark:text-secondary-400" />
          <span className="font-medium text-secondary-900 dark:text-white">
            {subject.name ?? `Subject ${index + 1}`}
          </span>
          {subject.isPrimary && (
            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-primary-100 text-primary-700 dark:bg-primary-900/20 dark:text-primary-300">
              Primary
            </span>
          )}
        </div>
        <div className="flex items-center space-x-2">
          {isEditing && (
            <>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onSetPrimary(index); }}
                title={subject.isPrimary ? 'Primary subject' : 'Set as primary'}
                className="p-1 rounded hover:bg-secondary-200 dark:hover:bg-secondary-700 transition-colors"
              >
                {subject.isPrimary ? (
                  <StarIconSolid className="w-4 h-4 text-warning-500" />
                ) : (
                  <StarIcon className="w-4 h-4 text-secondary-400 hover:text-warning-500" />
                )}
              </button>
              {canDelete && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onDelete(index); }}
                  className="p-1 rounded hover:bg-danger-100 dark:hover:bg-danger-900/20 text-danger-600 hover:text-danger-700 transition-colors"
                >
                  <TrashIcon className="w-4 h-4" />
                </button>
              )}
            </>
          )}
          {isExpanded ? (
            <ChevronUpIcon className="w-5 h-5 text-secondary-500" />
          ) : (
            <ChevronDownIcon className="w-5 h-5 text-secondary-500" />
          )}
        </div>
      </div>

      {isExpanded && (
        <CardContent className="pt-0 pb-4 px-4">
          {isEditing ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                    <UserIcon className="w-4 h-4 inline mr-1" />
                    Name
                  </label>
                  <input
                    type="text"
                    value={subject.name ?? ''}
                    onChange={(e) => onChange(index, 'name', e.target.value)}
                    placeholder="Full name"
                    className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white placeholder-secondary-400"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                    <EnvelopeIcon className="w-4 h-4 inline mr-1" />
                    Email
                  </label>
                  <input
                    type="email"
                    value={subject.email ?? ''}
                    onChange={(e) => onChange(index, 'email', e.target.value)}
                    placeholder="email@example.com"
                    className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white placeholder-secondary-400"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                    <PhoneIcon className="w-4 h-4 inline mr-1" />
                    Phone
                  </label>
                  <input
                    type="tel"
                    value={subject.phone ?? ''}
                    onChange={(e) => onChange(index, 'phone', e.target.value)}
                    placeholder="+1 (555) 000-0000"
                    className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white placeholder-secondary-400"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                    <BriefcaseIcon className="w-4 h-4 inline mr-1" />
                    Role/Position
                  </label>
                  <input
                    type="text"
                    value={subject.role ?? ''}
                    onChange={(e) => onChange(index, 'role', e.target.value)}
                    placeholder="e.g., Senior Developer"
                    className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white placeholder-secondary-400"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                  <BuildingOfficeIcon className="w-4 h-4 inline mr-1" />
                  Department
                </label>
                <input
                  type="text"
                  value={subject.department ?? ''}
                  onChange={(e) => onChange(index, 'department', e.target.value)}
                  placeholder="e.g., Engineering, Finance"
                  className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white placeholder-secondary-400"
                />
              </div>
              <RichTextEditor
                label="Additional Notes"
                value={subject.notes ?? ''}
                onChange={(html) => onChange(index, 'notes', html)}
                placeholder="Any additional information about this subject"
                minHeight={80}
              />
            </div>
          ) : (
            <div className="space-y-2">
              {!hasContent ? (
                <p className="text-sm text-secondary-500 dark:text-secondary-400 italic">
                  No information provided
                </p>
              ) : (
                <>
                  {subject.email && (
                    <div className="flex items-center space-x-2 text-sm">
                      <EnvelopeIcon className="w-4 h-4 text-secondary-500 dark:text-secondary-400" />
                      <span className="text-secondary-700 dark:text-secondary-300">{subject.email}</span>
                    </div>
                  )}
                  {subject.phone && (
                    <div className="flex items-center space-x-2 text-sm">
                      <PhoneIcon className="w-4 h-4 text-secondary-500 dark:text-secondary-400" />
                      <span className="text-secondary-700 dark:text-secondary-300">{subject.phone}</span>
                    </div>
                  )}
                  {subject.role && (
                    <div className="flex items-center space-x-2 text-sm">
                      <BriefcaseIcon className="w-4 h-4 text-secondary-500 dark:text-secondary-400" />
                      <span className="text-secondary-700 dark:text-secondary-300">{subject.role}</span>
                    </div>
                  )}
                  {subject.department && (
                    <div className="flex items-center space-x-2 text-sm">
                      <BuildingOfficeIcon className="w-4 h-4 text-secondary-500 dark:text-secondary-400" />
                      <span className="text-secondary-700 dark:text-secondary-300">{subject.department}</span>
                    </div>
                  )}
                  {subject.notes && (
                    <div className="mt-2">
                      <RichTextDisplay
                        content={subject.notes}
                        className="text-sm text-secondary-700 dark:text-secondary-300"
                        emptyMessage=""
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}

export default SubjectCard;
